<template>
  <v-card>
    <v-card-title>
      {{ fund.name }}

      <v-spacer></v-spacer>

      <v-chip v-if="fund.hidden" small color="warning" dark outlined>
        {{ $t("auditor.fundCard.hidden.hidden") }}
      </v-chip>

      <v-chip color="green" small dark outlined v-if="fund.precheck">{{
        $t("auditor.fundCard.precheck.label")
      }}</v-chip>

      <v-chip color="green" small dark outlined>{{ fund.state }}</v-chip>

      <v-menu v-if="$auth.may(['audit:finish'])" bottom left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" class="ml-1">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item v-if="fund.state !== 'NEW'" :to="{ name: 'auditFundBase', params: { id: fund.id } }">
            <v-list-item-content>
              <v-list-item-title>{{ $t("auditor.editBaseData.cta") }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-dialog
            v-if="fund.stepModel && fund.mayDisableStepModel"
            v-model="disableStepModelModal"
            persistent
            max-width="600"
          >
            <template #activator="{ on, attrs }">
              <v-list-item v-bind="attrs" v-on="on">
                <v-list-item-content>
                  <v-list-item-title>{{ $t("auditor.disableStepModel.cta") }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>

            <v-card>
              <v-card-title class="headline">
                {{ $t("auditor.disableStepModel.confirmation.title") }}
              </v-card-title>

              <v-card-text>
                <Markdown :value="$t('auditor.disableStepModel.confirmation.description')" />
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn color="warning" @click="disableStepModelModal = false">
                  {{ $t("auditor.disableStepModel.confirmation.cancel") }}
                </v-btn>

                <v-btn
                  color="primary"
                  @click="
                    disableStepModel();
                    disableStepModelModal = false;
                  "
                >
                  {{ $t("auditor.disableStepModel.confirmation.accept") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-dialog
            v-if="!fund.stepModel && fund.mayEnableStepModel"
            v-model="enableStepModelModal"
            persistent
            max-width="600"
          >
            <template #activator="{ on, attrs }">
              <v-list-item v-bind="attrs" v-on="on">
                <v-list-item-content>
                  <v-list-item-title>{{ $t("auditor.enableStepModel.cta") }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>

            <v-card>
              <v-card-title class="headline">
                {{ $t("auditor.enableStepModel.confirmation.title") }}
              </v-card-title>

              <v-card-text>
                <Markdown :value="$t('auditor.enableStepModel.confirmation.description')" />
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn color="warning" @click="enableStepModelModal = false">
                  {{ $t("auditor.enableStepModel.confirmation.cancel") }}
                </v-btn>

                <v-btn
                  color="primary"
                  @click="
                    enableStepModel();
                    enableStepModelModal = false;
                  "
                >
                  {{ $t("auditor.enableStepModel.confirmation.accept") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-dialog v-if="!fund.hidden" v-model="hideFundModal" persistent max-width="600">
            <template #activator="{ on, attrs }">
              <v-list-item v-bind="attrs" v-on="on">
                <v-list-item-content>
                  <v-list-item-title>{{ $t("auditor.hideFund.cta") }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>

            <v-card>
              <v-card-title class="headline">
                {{ $t("auditor.hideFund.confirmation.title") }}
              </v-card-title>

              <v-card-text>
                <Markdown :value="$t('auditor.hideFund.confirmation.description')" />
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn color="warning" @click="hideFundModal = false">
                  {{ $t("auditor.hideFund.confirmation.cancel") }}
                </v-btn>

                <v-btn
                  color="primary"
                  @click="
                    hideFund();
                    hideFundModal = false;
                  "
                >
                  {{ $t("auditor.hideFund.confirmation.accept") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-dialog v-if="fund.hidden" v-model="showFundModal" persistent max-width="600">
            <template #activator="{ on, attrs }">
              <v-list-item v-bind="attrs" v-on="on">
                <v-list-item-content>
                  <v-list-item-title>{{ $t("auditor.showFund.cta") }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>

            <v-card>
              <v-card-title class="headline">
                {{ $t("auditor.showFund.confirmation.title") }}
              </v-card-title>

              <v-card-text>
                <Markdown :value="$t('auditor.showFund.confirmation.description')" />
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn color="warning" @click="showFundModal = false">
                  {{ $t("auditor.showFund.confirmation.cancel") }}
                </v-btn>

                <v-btn
                  color="primary"
                  @click="
                    showFund();
                    showFundModal = false;
                  "
                >
                  {{ $t("auditor.showFund.confirmation.accept") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-dialog v-if="showDelete" v-model="deleteFundModal" persistent max-width="600">
            <template #activator="{ on, attrs }">
              <v-list-item v-bind="attrs" v-on="on">
                <v-list-item-content>
                  <v-list-item-title>{{ $t("auditor.deleteFund.cta") }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>

            <v-card>
              <v-card-title class="headline">
                {{ $t("auditor.deleteFund.confirmation.title") }}
              </v-card-title>

              <v-card-text>
                <Markdown :value="$t('auditor.deleteFund.confirmation.description')" />
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn color="warning" @click="deleteFundModal = false">
                  {{ $t("auditor.deleteFund.confirmation.cancel") }}
                </v-btn>

                <v-btn
                  color="primary"
                  @click="
                    deleteFund();
                    deleteFundModal = false;
                  "
                >
                  {{ $t("auditor.deleteFund.confirmation.accept") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-dialog
            v-if="fund.stepModelRequirementsState === 'SECOND_EDIT'"
            v-model="abortSecondEditModal"
            persistent
            max-width="600"
          >
            <template #activator="{ on, attrs }">
              <v-list-item v-bind="attrs" v-on="on">
                <v-list-item-content>
                  <v-list-item-title>{{ $t("auditor.abortSecondEdit.cta") }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>

            <v-card>
              <v-card-title class="headline">
                {{ $t("auditor.abortSecondEdit.confirmation.title") }}
              </v-card-title>

              <v-card-text>
                <Markdown :value="$t('auditor.abortSecondEdit.confirmation.description')" />
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn color="warning" @click="abortSecondEditModal = false">
                  {{ $t("auditor.abortSecondEdit.confirmation.cancel") }}
                </v-btn>

                <v-btn
                  color="primary"
                  @click="
                    abortSecondEdit();
                    abortSecondEditModal = false;
                  "
                  :loading="abortSecondEditLoading"
                >
                  {{ $t("auditor.abortSecondEdit.confirmation.accept") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-list>
      </v-menu>
    </v-card-title>

    <v-simple-table class="table-no-hover">
      <template #default>
        <tbody>
          <tr v-if="isYellow">
            <td width="50%" colspan="4">
              <v-alert color="yellow">
                {{ $t("auditor.fundCard.yellow.description") }}
              </v-alert>
            </td>
          </tr>

          <tr v-if="isOrange">
            <td width="50%" colspan="4">
              <v-alert color="orange" dark>
                {{ $t("auditor.fundCard.orange.description") }}
              </v-alert>
            </td>
          </tr>

          <tr v-if="fund.state !== 'DONE' && fund.minimalRequirementsState !== 'DONE'">
            <td>
              {{
                fund.precheck ? $t("auditor.fundCard.precheck.label") : $t("auditor.fundCard.minimalRequirements.label")
              }}
            </td>

            <td>
              <v-chip small outlined class="mr-1">
                {{ fund.minimalRequirementsState }}
              </v-chip>

              <v-chip small outlined class="mr-1">
                {{ minimalRequirementsProgress }} /
                {{ minimalRequirementsTotal }}
              </v-chip>

              <v-chip v-if="userFeedbackMinimal.length > 0" small outlined>
                {{
                  $t("auditor.fundCard.feedback.label", {
                    user: userFeedbackMinimal.length,
                    auditor: auditorFeedbackMinimal.length,
                  })
                }}
              </v-chip>
            </td>

            <td width="1%">
              <v-btn
                v-if="auditMinimal"
                :to="{ name: 'auditFundMinimal', params: { id: fund.id } }"
                color="secondary"
                icon
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </td>
          </tr>

          <tr v-if="fund.minimalAttachments.length > 0">
            <td>
              {{
                fund.precheck
                  ? $t("auditor.fundCard.precheckAttachments.label")
                  : $t("auditor.fundCard.minimalAttachments.label")
              }}
            </td>

            <td>
              {{ fund.minimalAttachments.length }}
            </td>

            <td width="1%">
              <v-dialog v-model="minimalAttachmentsDialog" persistent max-width="600">
                <template #activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" color="secondary" icon>
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                </template>

                <v-card>
                  <v-card-title class="headline">
                    {{ $t("auditor.download.confirmation.title") }}
                  </v-card-title>

                  <v-card-text>
                    <Markdown :value="$t('auditor.download.confirmation.description')" />
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn color="warning" @click="minimalAttachmentsDialog = false">
                      {{ $t("auditor.download.confirmation.cancel") }}
                    </v-btn>

                    <v-btn
                      color="primary"
                      @click="
                        download(fund.minimalAttachments);
                        minimalAttachmentsDialog = false;
                      "
                    >
                      {{ $t("auditor.download.confirmation.accept") }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </td>
          </tr>

          <tr v-if="fund.state !== 'DONE' && fund.stepModel && fund.stepModelRequirementsState !== 'DONE'">
            <td>
              {{ $t("auditor.fundCard.stepModelRequirements.label") }}
            </td>

            <td>
              <v-chip small outlined class="mr-1">
                {{ fund.stepModelRequirementsState }}
              </v-chip>

              <v-chip small outlined class="mr-1">
                {{ stepModelRequirementsProgress }} /
                {{ stepModelRequirementsTotal }}
              </v-chip>

              <v-chip v-if="userFeedbackStepModel.length > 0" small outlined>
                {{
                  $t("auditor.fundCard.feedback.label", {
                    user: userFeedbackStepModel.length,
                    auditor: auditorFeedbackStepModel.length,
                  })
                }}
              </v-chip>
            </td>

            <td width="1%">
              <v-btn
                v-if="auditStepModel"
                :to="{ name: 'auditFundStep', params: { id: fund.id } }"
                color="secondary"
                icon
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>

              <v-chip small color="red" dark v-if="!fund.stepModel">
                {{ $t("auditor.fundCard.stepModel.disabled") }}
              </v-chip>
            </td>
          </tr>

          <tr v-if="fund.stepModelAttachments.length > 0">
            <td>
              {{ $t("auditor.fundCard.stepModelAttachments.label") }}
            </td>

            <td>
              {{ fund.stepModelAttachments.length }}
            </td>

            <td width="1%">
              <v-dialog v-model="stepModelAttachmentsDialog" persistent max-width="600">
                <template #activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" color="secondary" icon>
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                </template>

                <v-card>
                  <v-card-title class="headline">
                    {{ $t("auditor.download.confirmation.title") }}
                  </v-card-title>

                  <v-card-text>
                    <Markdown :value="$t('auditor.download.confirmation.description')" />
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn color="warning" @click="stepModelAttachmentsDialog = false">
                      {{ $t("auditor.download.confirmation.cancel") }}
                    </v-btn>

                    <v-btn
                      color="primary"
                      @click="
                        download(fund.stepModelAttachments);
                        stepModelAttachmentsDialog = false;
                      "
                    >
                      {{ $t("auditor.download.confirmation.accept") }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </td>
          </tr>

          <tr v-if="fund.state !== 'NEW'">
            <td>
              {{ $t("auditor.fundCard.protocol.label") }}
            </td>

            <td></td>

            <td width="1%">
              <v-btn :to="{ name: 'auditFundProtocol', params: { id: fund.id } }" color="secondary" icon>
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </td>
          </tr>

          <tr v-if="fund.mayBeClosed && $auth.may(['admin:users', 'audit:users'])">
            <td>
              {{ $t("auditor.fundCard.userProvisionalReport.label") }}
            </td>

            <td></td>

            <td width="1%">
              <v-btn :to="{ name: 'auditUserFundProvisionalReport', params: { id: fund.id } }" color="secondary" icon>
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </td>
          </tr>

          <tr v-if="fund.state === 'DONE' && $auth.may(['admin:users', 'audit:users'])">
            <td>
              {{ $t("auditor.fundCard.userFinalReport.label") }}
            </td>

            <td></td>

            <td width="1%">
              <v-btn :to="{ name: 'userFinalReport', params: { id: fund.id } }" color="secondary" icon>
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </td>
          </tr>

          <tr v-if="fund.mayBeClosed && $auth.may(['admin:users', 'audit:users'])">
            <td>
              {{ $t("auditor.fundCard.provisionalReport.label") }}
            </td>

            <td></td>

            <td width="1%">
              <v-btn :to="{ name: 'auditFundProvisionalReport', params: { id: fund.id } }" color="secondary" icon>
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </td>
          </tr>

          <tr v-if="fund.state === 'DONE' && $auth.may(['admin:users', 'audit:users'])">
            <td>
              {{ $t("auditor.fundCard.finalReport.label") }}
            </td>

            <td></td>

            <td width="1%">
              <v-btn :to="{ name: 'auditFundFinalReport', params: { id: fund.id } }" color="secondary" icon>
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </td>
          </tr>

          <tr v-if="$auth.may(['audit:finish']) && fund.mayBeClosed">
            <td>
              {{ $t("auditor.fundCard.fund.close") }}
            </td>

            <td></td>

            <td width="1%">
              <v-btn @click="closeFund" color="secondary" icon>
                <v-icon>mdi-check</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </v-card>
</template>

<script>
import { includes, filter, length, pluck } from "ramda";
import { saveAs } from "file-saver";

import DeleteFundMutation from "@/gql/mutations/DeleteFundMutation.gql";
import FinishAuditMutation from "@/gql/mutations/FinishAuditMutation.gql";
import HideFundMutation from "@/gql/mutations/HideFundMutation.gql";
import ShowFundMutation from "@/gql/mutations/ShowFundMutation.gql";
import EnableStepModelMutation from "@/gql/mutations/EnableStepModelMutation.gql";
import DisableStepModelMutation from "@/gql/mutations/DisableStepModelMutation.gql";
import AbortSecondEditMutation from "@/gql/mutations/AbortSecondEditMutation.gql";
import mayAddFundsToAuditPeriod from "@fng/common/mayAddFundsToAuditPeriod";
import normalizeFeedback from "@fng/common/normalizeFeedback";
import OptionsQuery from "@/gql/queries/auditor/OptionsQuery.gql";

export default {
  props: {
    fund: {
      type: Object,
      required: true,
    },

    auditPeriod: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      minimalAttachmentsDialog: false,
      stepModelAttachmentsDialog: false,
      abortSecondEditModal: false,
      abortSecondEditLoading: false,
      disableStepModelModal: false,
      enableStepModelModal: false,
      hideFundModal: false,
      showFundModal: false,
      deleteFundModal: false,
      options: null,
    };
  },

  apollo: {
    options: {
      query: OptionsQuery,
      fetchPolicy: "cache-and-network",
    },
  },

  computed: {
    auditMinimal() {
      return this.fund.state !== "DONE" && includes(this.fund.minimalRequirementsState, ["AUDIT", "FINAL_AUDIT"]);
    },

    auditStepModel() {
      return (
        this.fund.state !== "DONE" &&
        this.fund.stepModel &&
        includes(this.fund.stepModelRequirementsState, ["AUDIT", "SECOND_AUDIT", "INTERVIEW_AUDIT", "FINAL_AUDIT"])
      );
    },

    minimalRequirementsTotal() {
      return length(this.fund.minimalRequirements);
    },

    minimalRequirementsProgress() {
      return length(filter((req) => includes(req.state, ["DONE"]), this.fund.minimalRequirements));
    },

    stepModelRequirementsTotal() {
      return length(this.fund.stepModelRequirements);
    },

    stepModelRequirementsProgress() {
      return length(filter((req) => includes(req.state, ["DONE"]), this.fund.stepModelRequirements));
    },

    showDelete() {
      return this.$auth.may(["admin:users"]) && mayAddFundsToAuditPeriod([this.fund]);
    },

    userFeedbackMinimal() {
      return filter(normalizeFeedback, pluck("userFeedback", this.fund.minimalRequirements));
    },

    userFeedbackStepModel() {
      return filter(normalizeFeedback, pluck("userFeedback", this.fund.stepModelRequirements));
    },

    auditorFeedbackMinimal() {
      return filter(normalizeFeedback, pluck("auditorFeedback", this.fund.minimalRequirements));
    },

    auditorFeedbackStepModel() {
      return filter(normalizeFeedback, pluck("auditorFeedback", this.fund.stepModelRequirements));
    },

    yellowDeadline() {
      const yellowDeadline = this.options?.find(
        (option) => option.key === (this.fund.precheck ? "precheckYellowDeadline" : "yellowDeadline")
      )?.value;

      return new Date(yellowDeadline);
    },

    isYellow() {
      if (this.fund.state === "DONE") return false;
      if (this.auditPeriod.year !== new Date().getFullYear()) return false;
      if (this.isOrange) return false;

      const initialEdit = this.fund.stepModel ? this.fund.initialStepModelEditAt : this.fund.initialMinimalEditAt;

      if (!initialEdit) {
        return new Date() > this.yellowDeadline;
      }

      const initialEditDate = new Date(initialEdit);

      return this.yellowDeadline && this.yellowDeadline < initialEditDate;
    },

    orangeDeadline() {
      const orangeDeadline = this.options?.find(
        (option) => option.key === (this.fund.precheck ? "precheckOrangeDeadline" : "orangeDeadline")
      )?.value;

      return new Date(orangeDeadline);
    },

    isOrange() {
      if (this.fund.state === "DONE") return false;
      if (this.auditPeriod.year !== new Date().getFullYear()) return false;

      const initialEdit = this.fund.stepModel ? this.fund.initialStepModelEditAt : this.fund.initialMinimalEditAt;

      if (!initialEdit) {
        return new Date() > this.orangeDeadline;
      }

      const initialEditDate = new Date(initialEdit);

      return this.orangeDeadline && this.orangeDeadline < initialEditDate;
    },
  },

  methods: {
    closeFund() {
      this.$apollo.mutate({
        mutation: FinishAuditMutation,
        variables: {
          id: this.fund.id,
        },
      });
    },

    deleteFund() {
      this.$apollo.mutate({
        mutation: DeleteFundMutation,
        variables: {
          id: this.fund.id,
        },
      });
    },

    hideFund() {
      this.$apollo.mutate({
        mutation: HideFundMutation,
        variables: {
          id: this.fund.id,
        },
      });
    },

    showFund() {
      this.$apollo.mutate({
        mutation: ShowFundMutation,
        variables: {
          id: this.fund.id,
        },
      });
    },

    enableStepModel() {
      this.$apollo.mutate({
        mutation: EnableStepModelMutation,
        variables: {
          id: this.fund.id,
        },
      });
    },

    disableStepModel() {
      this.$apollo.mutate({
        mutation: DisableStepModelMutation,
        variables: {
          id: this.fund.id,
        },
      });
    },

    download(attachments) {
      attachments.forEach((attachment, index) => {
        setTimeout(
          () =>
            fetch(attachment.file.url)
              .then((res) => res.blob())
              .then((blob) => {
                saveAs(blob, attachment.file.name);
              }),
          index * 500
        );
      });
    },

    async abortSecondEdit() {
      try {
        this.abortSecondEditLoading = true;

        await this.$apollo.mutate({
          mutation: AbortSecondEditMutation,
          variables: {
            id: this.fund.id,
          },
        });
      } finally {
        this.abortSecondEditLoading = false;
      }
    },
  },
};
</script>
